import { viewportObserver } from '../../utils/utils'
import ProductCardColorSwatches from './product-card-color-swatches'
import { Nullable } from '../../types/common'
import Card from '../card/card'
import { hideElement } from '../../utils/dom'
import QuickViewComponent from '../quick-view/quick-view'
import { debounce } from '../../utils/debounce'

class ProductCard extends Card {
  resizeObserver: ResizeObserver
  lastButtonsWidth: number
  loadedImageCount: number
  quickViewTrigger: Nullable<HTMLElement>
  loader: Nullable<HTMLElement>
  buttons: Nullable<HTMLElement>
  featuredImages: HTMLImageElement[]
  swatches: Nullable<ProductCardColorSwatches>
  quickView: Nullable<QuickViewComponent>

  constructor() {
    super()

    this.lastButtonsWidth = 0
    this.loadedImageCount = 0

    this.quickViewTrigger = this.querySelector(
      '[data-product-card-quick-view-button]',
    )
    this.loader = this.querySelector('[data-product-card-preloader]')

    this.buttons = this.querySelector('[data-product-card-buttons]')
    this.featuredImages = [
      ...this.querySelectorAll('[data-product-card-featured-image]'),
    ] as HTMLImageElement[]
    this.swatches = this.querySelector('[data-product-card-color-swatches]')
    this.quickView = document.getElementById('Quick-view') as QuickViewComponent

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)

    viewportObserver.observe(this)
  }

  connectedCallback(): void {
    this.featuredImages.forEach(image => {
      image.addEventListener('load', this.handleImageLoad)
    })

    if (this.quickViewTrigger) {
      this.quickViewTrigger.addEventListener(
        'mouseenter',
        this.handleMouseEnter,
      )
      this.quickViewTrigger.addEventListener(
        'click',
        this.handleQuickViewTriggerClick,
      )
    }
  }

  disconnectedCallback(): void {
    this.featuredImages.forEach(image => {
      image.removeEventListener('load', this.handleImageLoad)
    })

    this.resizeObserver.disconnect()

    viewportObserver.unobserve(this)

    if (this.quickViewTrigger) {
      this.quickViewTrigger.removeEventListener(
        'mouseenter',
        this.handleMouseEnter,
      )
      this.quickViewTrigger.removeEventListener(
        'click',
        this.handleQuickViewTriggerClick,
      )
    }
  }

  handleMouseEnter = (): void => {
    const url = this.dataset.url
    const id = this.dataset.id

    if (!this.quickView) {
      return
    }

    if (url && id) {
      this.quickView.requestProductFromUrl(url, id)
    }
  }

  handleQuickViewTriggerClick = (): void => {
    const url = this.dataset.url

    if (!this.quickView) {
      return
    }

    if (url) {
      this.quickView.openAndRenderProductByUrl(url)
    }
  }

  handleResize = debounce((): void => {
    if (this.buttons) {
      this.setButtonsOverflow()
    }

    if (this.swatches) {
      this.swatches.update()
    }
  }, 100)

  handleImageLoad = (): void => {
    this.loadedImageCount += 1

    if (this.loadedImageCount === this.featuredImages?.length) {
      this.removeLoader()

      this.featuredImages.forEach(image => {
        image.removeEventListener('load', this.handleImageLoad)
      })
    }
  }

  setButtonsOverflow(): void {
    if (!this.buttons) {
      return
    }

    if (this.offsetWidth < this.lastButtonsWidth) {
      return
    }

    const isOverflowed = this.buttons.offsetWidth > this.offsetWidth

    if (isOverflowed && this.buttons.offsetWidth > this.lastButtonsWidth) {
      this.lastButtonsWidth = this.buttons.offsetWidth
    }

    this.buttons.classList.toggle(
      'product-card__btns--overflowed',
      isOverflowed,
    )
  }

  removeLoader(): void {
    if (!this.loader) {
      return
    }

    viewportObserver.unobserve(this)

    hideElement(this.loader)
  }
}

export default ProductCard
